import { Mail, Phone, MapPin, Calendar as CalIcon, User as UserIcon, Globe } from "lucide-react";

/**
 * DIN A4 Preview Component for Bewerbungs-Tool
 * Renders Deckblatt, Anschreiben, and Lebenslauf in 3 design templates:
 * - Modern (sans-serif, accent bar, minimal)
 * - Klassisch (serif, traditional centered)
 * - Kreativ (two-column with sidebar)
 *
 * A4 dimensions: 210mm x 297mm (1mm = 3.7795px @ 96dpi)
 * The pages render at exact A4 size; container scales them visually.
 */

// ============== TEMPLATE: MODERN ==============
const ModernCover = ({ data }) => (
  <div className="a4-page a4-page-fixed font-sans">
    <div style={{ height: "12mm", background: "#0ea5e9", marginBottom: "20mm" }} />
    <div style={{ padding: "0 20mm" }}>
      <h1 style={{ fontSize: "48pt", fontWeight: 300, color: "#0f172a", letterSpacing: "-0.02em", lineHeight: 1, margin: 0 }}>
        {data.cover_title || "Bewerbung"}
      </h1>
      <div style={{ width: "60mm", height: "2px", background: "#0ea5e9", margin: "8mm 0" }} />
      <p style={{ fontSize: "18pt", color: "#475569", margin: "0 0 4mm 0" }}>
        als <strong style={{ color: "#0f172a", fontWeight: 600 }}>{data.cover_position || "—"}</strong>
      </p>
      {data.cover_company && (
        <p style={{ fontSize: "14pt", color: "#64748b", margin: 0 }}>
          bei {data.cover_company}
        </p>
      )}
    </div>
    {data.cover_photo && (
      <div style={{ position: "absolute", right: "20mm", top: "30mm" }}>
        <img src={data.cover_photo} alt="" style={{ width: "55mm", height: "70mm", objectFit: "cover", borderRadius: "2mm", boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }} />
      </div>
    )}
    <div style={{ position: "absolute", bottom: "20mm", left: "20mm", right: "20mm", paddingTop: "8mm", borderTop: "1px solid #e2e8f0", fontSize: "10pt", color: "#475569", display: "flex", justifyContent: "space-between" }}>
      <div>
        <div style={{ fontWeight: 600, color: "#0f172a", fontSize: "11pt" }}>{data.personal_name}</div>
        {data.personal_address && <div>{data.personal_address}</div>}
      </div>
      <div style={{ textAlign: "right" }}>
        {data.personal_email && <div>{data.personal_email}</div>}
        {data.personal_phone && <div>{data.personal_phone}</div>}
      </div>
    </div>
  </div>
);

const ModernLetter = ({ data }) => (
  <div className="a4-page a4-page-fixed font-sans" style={{ padding: "20mm 20mm", color: "#0f172a", fontSize: "11pt", lineHeight: 1.5 }}>
    {/* Sender (top-left) */}
    <div style={{ fontSize: "9pt", color: "#64748b", borderBottom: "1px solid #e2e8f0", paddingBottom: "3mm", marginBottom: "12mm" }}>
      {[data.personal_name, data.personal_address, data.personal_phone, data.personal_email].filter(Boolean).join(" · ")}
    </div>
    {/* Recipient */}
    <div style={{ marginBottom: "15mm" }}>
      {data.letter_recipient_company && <div style={{ fontWeight: 600 }}>{data.letter_recipient_company}</div>}
      {data.letter_recipient_name && <div>{data.letter_recipient_name}</div>}
      {data.letter_recipient_address && (
        <div style={{ whiteSpace: "pre-line" }}>{data.letter_recipient_address}</div>
      )}
    </div>
    {/* Date */}
    <div style={{ textAlign: "right", marginBottom: "10mm", color: "#475569" }}>
      {data.letter_date ? new Date(data.letter_date).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" }) : ""}
    </div>
    {/* Subject */}
    <div style={{ fontWeight: 700, fontSize: "12pt", marginBottom: "8mm", color: "#0f172a" }}>
      {data.letter_subject || `Bewerbung als ${data.cover_position || ""}`}
    </div>
    {/* Body */}
    <div style={{ whiteSpace: "pre-line", textAlign: "justify" }}>
      {data.letter_content || "Sehr geehrte Damen und Herren,\n\n..."}
    </div>
    {/* Signature */}
    <div style={{ marginTop: "15mm" }}>
      <div>Mit freundlichen Grüßen</div>
      <div style={{ marginTop: "10mm", fontWeight: 600 }}>{data.personal_name}</div>
    </div>
  </div>
);

const ModernCV = ({ data }) => (
  <div className="a4-page a4-page-flow font-sans" style={{ padding: "20mm", color: "#0f172a", fontSize: "10pt", lineHeight: 1.5 }}>
    {/* Header */}
    <div style={{ display: "flex", gap: "10mm", alignItems: "flex-start", paddingBottom: "8mm", borderBottom: "3px solid #0ea5e9", marginBottom: "10mm" }}>
      {data.cover_photo && (
        <img src={data.cover_photo} alt="" style={{ width: "30mm", height: "38mm", objectFit: "cover", borderRadius: "2mm", flexShrink: 0 }} />
      )}
      <div style={{ flex: 1 }}>
        <h1 style={{ fontSize: "26pt", fontWeight: 300, margin: 0, letterSpacing: "-0.02em" }}>
          {data.personal_name || "Vor- und Nachname"}
        </h1>
        <div style={{ fontSize: "11pt", color: "#475569", marginTop: "2mm" }}>Lebenslauf</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2mm", marginTop: "4mm", fontSize: "9pt", color: "#64748b" }}>
          {data.personal_email && <div><Mail size={10} style={{ display: "inline", marginRight: "2mm" }} />{data.personal_email}</div>}
          {data.personal_phone && <div><Phone size={10} style={{ display: "inline", marginRight: "2mm" }} />{data.personal_phone}</div>}
          {data.personal_address && <div><MapPin size={10} style={{ display: "inline", marginRight: "2mm" }} />{data.personal_address}</div>}
          {data.personal_birthdate && <div><CalIcon size={10} style={{ display: "inline", marginRight: "2mm" }} />{data.personal_birthdate}{data.personal_birthplace ? ` · ${data.personal_birthplace}` : ""}</div>}
        </div>
      </div>
    </div>
    <CVSections data={data} accentColor="#0ea5e9" />
  </div>
);

// ============== TEMPLATE: KLASSISCH ==============
const KlassischCover = ({ data }) => (
  <div className="a4-page a4-page-fixed" style={{ fontFamily: "'Times New Roman', Georgia, serif", padding: "30mm 25mm", color: "#1a1a1a", textAlign: "center" }}>
    <div style={{ borderTop: "3px double #1a1a1a", borderBottom: "3px double #1a1a1a", padding: "12mm 0", marginTop: "30mm" }}>
      <h1 style={{ fontSize: "42pt", fontWeight: 400, margin: 0, letterSpacing: "0.05em" }}>
        {data.cover_title || "Bewerbung"}
      </h1>
    </div>
    <div style={{ marginTop: "20mm", fontSize: "16pt", fontStyle: "italic", color: "#333" }}>
      als
    </div>
    <div style={{ fontSize: "22pt", fontWeight: 600, marginTop: "5mm", color: "#1a1a1a" }}>
      {data.cover_position || "—"}
    </div>
    {data.cover_company && (
      <div style={{ fontSize: "14pt", marginTop: "8mm", color: "#444" }}>
        bei der {data.cover_company}
      </div>
    )}
    {data.cover_photo && (
      <div style={{ marginTop: "20mm" }}>
        <img src={data.cover_photo} alt="" style={{ width: "55mm", height: "70mm", objectFit: "cover", border: "2px solid #1a1a1a" }} />
      </div>
    )}
    <div style={{ position: "absolute", bottom: "25mm", left: "25mm", right: "25mm", borderTop: "1px solid #999", paddingTop: "6mm", fontSize: "10pt" }}>
      <div style={{ fontWeight: 600 }}>{data.personal_name}</div>
      <div>{[data.personal_address, data.personal_phone, data.personal_email].filter(Boolean).join(" · ")}</div>
    </div>
  </div>
);

const KlassischLetter = ({ data }) => (
  <div className="a4-page a4-page-fixed" style={{ fontFamily: "'Times New Roman', Georgia, serif", padding: "25mm", color: "#1a1a1a", fontSize: "11pt", lineHeight: 1.6 }}>
    <div style={{ textAlign: "right", fontSize: "10pt", marginBottom: "15mm" }}>
      <div style={{ fontWeight: 600 }}>{data.personal_name}</div>
      {data.personal_address && <div>{data.personal_address}</div>}
      {data.personal_phone && <div>Tel.: {data.personal_phone}</div>}
      {data.personal_email && <div>E-Mail: {data.personal_email}</div>}
    </div>
    <div style={{ marginBottom: "12mm" }}>
      {data.letter_recipient_company && <div style={{ fontWeight: 600 }}>{data.letter_recipient_company}</div>}
      {data.letter_recipient_name && <div>z. Hd. {data.letter_recipient_name}</div>}
      {data.letter_recipient_address && <div style={{ whiteSpace: "pre-line" }}>{data.letter_recipient_address}</div>}
    </div>
    <div style={{ textAlign: "right", marginBottom: "8mm" }}>
      {data.letter_date ? new Date(data.letter_date).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" }) : ""}
    </div>
    <div style={{ fontWeight: 700, fontSize: "12pt", marginBottom: "8mm", textDecoration: "underline" }}>
      {data.letter_subject || `Bewerbung als ${data.cover_position || ""}`}
    </div>
    <div style={{ whiteSpace: "pre-line", textAlign: "justify" }}>
      {data.letter_content || "Sehr geehrte Damen und Herren,\n\n..."}
    </div>
    <div style={{ marginTop: "15mm" }}>
      <div>Mit freundlichen Grüßen</div>
      <div style={{ marginTop: "12mm", fontStyle: "italic", fontSize: "13pt" }}>{data.personal_name}</div>
    </div>
  </div>
);

const KlassischCV = ({ data }) => (
  <div className="a4-page a4-page-flow" style={{ fontFamily: "'Times New Roman', Georgia, serif", padding: "20mm 25mm", color: "#1a1a1a", fontSize: "10.5pt", lineHeight: 1.5 }}>
    <div style={{ textAlign: "center", marginBottom: "8mm", paddingBottom: "6mm", borderBottom: "2px solid #1a1a1a" }}>
      <h1 style={{ fontSize: "28pt", fontWeight: 400, margin: 0, letterSpacing: "0.04em" }}>LEBENSLAUF</h1>
      <div style={{ fontSize: "16pt", marginTop: "4mm", fontWeight: 600 }}>{data.personal_name || "Vor- und Nachname"}</div>
    </div>
    <div style={{ display: "flex", gap: "8mm", marginBottom: "8mm" }}>
      {data.cover_photo && (
        <img src={data.cover_photo} alt="" style={{ width: "30mm", height: "38mm", objectFit: "cover", border: "1px solid #1a1a1a" }} />
      )}
      <div style={{ flex: 1, fontSize: "10pt" }}>
        <SerifInfoRow label="Adresse" value={data.personal_address} />
        <SerifInfoRow label="Telefon" value={data.personal_phone} />
        <SerifInfoRow label="E-Mail" value={data.personal_email} />
        <SerifInfoRow label="Geburtsdatum" value={data.personal_birthdate} />
        <SerifInfoRow label="Geburtsort" value={data.personal_birthplace} />
        <SerifInfoRow label="Nationalität" value={data.personal_nationality} />
      </div>
    </div>
    <CVSections data={data} accentColor="#1a1a1a" serif />
  </div>
);

const SerifInfoRow = ({ label, value }) => value ? (
  <div style={{ display: "flex", marginBottom: "1.5mm" }}>
    <div style={{ width: "30mm", fontWeight: 600 }}>{label}:</div>
    <div>{value}</div>
  </div>
) : null;

// ============== TEMPLATE: KREATIV ==============
const KreativCover = ({ data }) => (
  <div className="a4-page a4-page-fixed font-sans" style={{ display: "flex", color: "#fff" }}>
    <div style={{ width: "75mm", background: "linear-gradient(180deg, #6366f1 0%, #8b5cf6 100%)", padding: "25mm 12mm", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
      {data.cover_photo && (
        <img src={data.cover_photo} alt="" style={{ width: "50mm", height: "62mm", objectFit: "cover", borderRadius: "50%", boxShadow: "0 8px 24px rgba(0,0,0,0.3)" }} />
      )}
      <div style={{ fontSize: "9pt", lineHeight: 1.6 }}>
        <div style={{ fontWeight: 700, fontSize: "11pt", marginBottom: "4mm", letterSpacing: "0.1em", textTransform: "uppercase" }}>Kontakt</div>
        {data.personal_email && <div style={{ marginBottom: "2mm" }}><Mail size={9} style={{ display: "inline", marginRight: "2mm" }} />{data.personal_email}</div>}
        {data.personal_phone && <div style={{ marginBottom: "2mm" }}><Phone size={9} style={{ display: "inline", marginRight: "2mm" }} />{data.personal_phone}</div>}
        {data.personal_address && <div style={{ marginBottom: "2mm" }}><MapPin size={9} style={{ display: "inline", marginRight: "2mm" }} />{data.personal_address}</div>}
      </div>
    </div>
    <div style={{ flex: 1, padding: "30mm 18mm", color: "#1e293b", display: "flex", flexDirection: "column", justifyContent: "center" }}>
      <div style={{ fontSize: "11pt", color: "#8b5cf6", letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 700, marginBottom: "6mm" }}>
        {data.cover_title || "Bewerbung"}
      </div>
      <h1 style={{ fontSize: "38pt", fontWeight: 800, margin: 0, lineHeight: 1, color: "#0f172a" }}>
        {data.personal_name?.split(" ")[0] || "Vor"}
      </h1>
      <h1 style={{ fontSize: "38pt", fontWeight: 800, margin: 0, lineHeight: 1, color: "#8b5cf6" }}>
        {data.personal_name?.split(" ").slice(1).join(" ") || "Name"}
      </h1>
      <div style={{ width: "30mm", height: "3mm", background: "#0f172a", margin: "8mm 0" }} />
      <div style={{ fontSize: "14pt", color: "#475569", fontWeight: 500 }}>
        {data.cover_position || "Position"}
      </div>
      {data.cover_company && (
        <div style={{ fontSize: "11pt", color: "#64748b", marginTop: "3mm" }}>
          @ {data.cover_company}
        </div>
      )}
    </div>
  </div>
);

const KreativLetter = ({ data }) => (
  <div className="a4-page font-sans" style={{ padding: "20mm", color: "#1e293b", fontSize: "10.5pt", lineHeight: 1.55 }}>
    <div style={{ borderLeft: "4px solid #8b5cf6", paddingLeft: "8mm", marginBottom: "15mm" }}>
      <div style={{ fontSize: "16pt", fontWeight: 800, color: "#0f172a" }}>{data.personal_name}</div>
      <div style={{ fontSize: "9pt", color: "#64748b", marginTop: "1mm" }}>
        {[data.personal_address, data.personal_phone, data.personal_email].filter(Boolean).join("  ·  ")}
      </div>
    </div>
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10mm" }}>
      <div>
        {data.letter_recipient_company && <div style={{ fontWeight: 600 }}>{data.letter_recipient_company}</div>}
        {data.letter_recipient_name && <div>{data.letter_recipient_name}</div>}
        {data.letter_recipient_address && <div style={{ whiteSpace: "pre-line" }}>{data.letter_recipient_address}</div>}
      </div>
      <div style={{ color: "#8b5cf6", fontWeight: 600 }}>
        {data.letter_date ? new Date(data.letter_date).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" }) : ""}
      </div>
    </div>
    <div style={{ background: "#f5f3ff", padding: "5mm 6mm", borderRadius: "2mm", fontWeight: 700, fontSize: "12pt", marginBottom: "8mm", color: "#5b21b6" }}>
      {data.letter_subject || `Bewerbung als ${data.cover_position || ""}`}
    </div>
    <div style={{ whiteSpace: "pre-line", textAlign: "justify" }}>
      {data.letter_content || "Sehr geehrte Damen und Herren,\n\n..."}
    </div>
    <div style={{ marginTop: "15mm" }}>
      <div>Mit freundlichen Grüßen</div>
      <div style={{ marginTop: "10mm", fontWeight: 700, color: "#8b5cf6" }}>{data.personal_name}</div>
    </div>
  </div>
);

const KreativCV = ({ data }) => (
  <div className="a4-page a4-page-flow font-sans" style={{ display: "flex", color: "#1e293b", fontSize: "9.5pt", lineHeight: 1.5 }}>
    {/* Sidebar */}
    <div style={{ width: "70mm", background: "#0f172a", color: "#fff", padding: "15mm 10mm" }}>
      {data.cover_photo && (
        <img src={data.cover_photo} alt="" style={{ width: "50mm", height: "50mm", objectFit: "cover", borderRadius: "50%", margin: "0 auto 8mm", display: "block", border: "3px solid #8b5cf6" }} />
      )}
      <SidebarSection title="Kontakt" color="#a78bfa">
        {data.personal_email && <div style={{ marginBottom: "2mm", fontSize: "8.5pt", wordBreak: "break-all" }}><Mail size={9} style={{ display: "inline", marginRight: "2mm" }} />{data.personal_email}</div>}
        {data.personal_phone && <div style={{ marginBottom: "2mm", fontSize: "8.5pt" }}><Phone size={9} style={{ display: "inline", marginRight: "2mm" }} />{data.personal_phone}</div>}
        {data.personal_address && <div style={{ marginBottom: "2mm", fontSize: "8.5pt" }}><MapPin size={9} style={{ display: "inline", marginRight: "2mm" }} />{data.personal_address}</div>}
      </SidebarSection>
      <SidebarSection title="Persönlich" color="#a78bfa">
        {data.personal_birthdate && <div style={{ fontSize: "8.5pt", marginBottom: "1mm" }}>* {data.personal_birthdate}{data.personal_birthplace ? ` in ${data.personal_birthplace}` : ""}</div>}
        {data.personal_nationality && <div style={{ fontSize: "8.5pt" }}><Globe size={9} style={{ display: "inline", marginRight: "2mm" }} />{data.personal_nationality}</div>}
      </SidebarSection>
      {(data.cv_skills || []).length > 0 && (
        <SidebarSection title="Kenntnisse" color="#a78bfa">
          {(data.cv_skills || []).map((s, i) => (
            <div key={i} style={{ fontSize: "8.5pt", marginBottom: "1.5mm" }}>{typeof s === "string" ? s : (s.name || "")}</div>
          ))}
        </SidebarSection>
      )}
      {(data.cv_languages || []).length > 0 && (
        <SidebarSection title="Sprachen" color="#a78bfa">
          {(data.cv_languages || []).map((l, i) => (
            <div key={i} style={{ fontSize: "8.5pt", marginBottom: "1.5mm" }}>
              <strong>{l.language || l.name}</strong>{l.level ? ` — ${l.level}` : ""}
            </div>
          ))}
        </SidebarSection>
      )}
    </div>
    {/* Main */}
    <div style={{ flex: 1, padding: "15mm 12mm" }}>
      <h1 style={{ fontSize: "26pt", fontWeight: 800, margin: 0, color: "#0f172a", lineHeight: 1 }}>{data.personal_name || "Lebenslauf"}</h1>
      <div style={{ fontSize: "11pt", color: "#8b5cf6", marginTop: "2mm", fontWeight: 600 }}>Lebenslauf</div>
      <div style={{ height: "2px", background: "#8b5cf6", width: "20mm", margin: "5mm 0" }} />
      <CVSections data={data} accentColor="#8b5cf6" compact />
    </div>
  </div>
);

const SidebarSection = ({ title, color, children }) => (
  <div style={{ marginBottom: "8mm" }}>
    <div style={{ fontWeight: 700, fontSize: "10pt", letterSpacing: "0.1em", textTransform: "uppercase", color, marginBottom: "3mm", borderBottom: "1px solid rgba(255,255,255,0.2)", paddingBottom: "1.5mm" }}>{title}</div>
    {children}
  </div>
);

// ============== SHARED CV SECTIONS ==============
const CVSections = ({ data, accentColor, serif, compact }) => {
  const Section = ({ title, children }) => (
    <div className="a4-keep-together" style={{ marginBottom: compact ? "5mm" : "7mm" }}>
      <div className="a4-section-header" style={{ fontSize: "12pt", fontWeight: 700, color: accentColor, marginBottom: "3mm", paddingBottom: "1.5mm", borderBottom: `1.5px solid ${accentColor}`, textTransform: serif ? "none" : "uppercase", letterSpacing: serif ? "0" : "0.05em" }}>{title}</div>
      {children}
    </div>
  );
  const Entry = ({ period, primary, secondary, details }) => (
    <div className="a4-cv-entry" style={{ marginBottom: "4mm", display: "flex", gap: "5mm" }}>
      <div style={{ width: "32mm", fontWeight: 600, color: "#475569", fontSize: "9pt", flexShrink: 0 }}>{period || ""}</div>
      <div style={{ flex: 1 }}>
        {primary && <div style={{ fontWeight: 700, color: "#0f172a" }}>{primary}</div>}
        {secondary && <div style={{ color: "#475569", fontStyle: "italic", marginBottom: "1mm" }}>{secondary}</div>}
        {details && <div style={{ color: "#475569", whiteSpace: "pre-line", fontSize: "9pt" }}>{details}</div>}
      </div>
    </div>
  );

  return (
    <>
      {(data.cv_education || []).length > 0 && (
        <Section title="Ausbildung">
          {data.cv_education.map((e, i) => (
            <Entry key={i} period={e.period} primary={e.degree} secondary={e.institution} details={e.details} />
          ))}
        </Section>
      )}
      {(data.cv_experience || []).length > 0 && (
        <Section title="Berufserfahrung">
          {data.cv_experience.map((e, i) => (
            <Entry key={i} period={e.period} primary={e.position || e.title} secondary={e.company} details={e.tasks || e.description} />
          ))}
        </Section>
      )}
      {!compact && (data.cv_skills || []).length > 0 && (
        <Section title="Kenntnisse">
          <div style={{ display: "flex", flexWrap: "wrap", gap: "2mm" }}>
            {(data.cv_skills || []).map((s, i) => (
              <span key={i} style={{ background: serif ? "#f1f5f9" : `${accentColor}15`, color: serif ? "#1a1a1a" : accentColor, padding: "1mm 3mm", borderRadius: "1mm", fontSize: "9pt", fontWeight: 500 }}>
                {typeof s === "string" ? s : (s.name || "")}
              </span>
            ))}
          </div>
        </Section>
      )}
      {!compact && (data.cv_languages || []).length > 0 && (
        <Section title="Sprachen">
          {data.cv_languages.map((l, i) => (
            <div key={i} style={{ marginBottom: "1.5mm" }}><strong>{l.language || l.name}</strong>{l.level ? ` — ${l.level}` : ""}</div>
          ))}
        </Section>
      )}
      {data.cv_hobbies && (
        <Section title="Hobbys & Interessen">
          <div style={{ color: "#475569" }}>{data.cv_hobbies}</div>
        </Section>
      )}
    </>
  );
};

// ============== MAIN EXPORT ==============
const TEMPLATES = {
  modern: { Cover: ModernCover, Letter: ModernLetter, CV: ModernCV, label: "Modern" },
  klassisch: { Cover: KlassischCover, Letter: KlassischLetter, CV: KlassischCV, label: "Klassisch" },
  kreativ: { Cover: KreativCover, Letter: KreativLetter, CV: KreativCV, label: "Kreativ" },
};

export const A4Document = ({ data, template = "modern" }) => {
  const T = TEMPLATES[template] || TEMPLATES.modern;
  return (
    <div className="a4-document" data-testid={`a4-document-${template}`}>
      <T.Cover data={data} />
      <T.Letter data={data} />
      <T.CV data={data} />
    </div>
  );
};

export const TEMPLATE_OPTIONS = [
  { id: "modern", label: "Modern", desc: "Klare Linien, Akzentfarben, sans-serif." },
  { id: "klassisch", label: "Klassisch", desc: "Traditionell, serifenbetont, formell." },
  { id: "kreativ", label: "Kreativ", desc: "Zwei Spalten mit farbiger Sidebar." },
];

export default A4Document;
